/** Public shop locale code (shop-configurable). */
export type ShopStorefrontLocale = string

export const SHOP_STOREFRONT_LOCALE_STORAGE_KEY = 'shop_storefront_locale'

export const DEFAULT_SHOP_LOCALE_OPTIONS: { value: ShopStorefrontLocale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-Hant', label: '繁體中文' },
]

/** Align with server `normalizeContentLocale` so API ?locale= matches DB i18n rows. */
export function normalizeShopLocale(raw: string | null | undefined): ShopStorefrontLocale {
  if (raw == null) return 'en'
  const v = String(raw).trim()
  if (!v) return 'en'
  const lower = v.toLowerCase().replace(/_/g, '-')
  if (lower === 'zh-hant' || lower === 'zh-tw' || lower === 'zh-hk') return 'zh-Hant'
  if (lower === 'zh-cn' || lower === 'zh') return 'zh-CN'
  const ascii = v.replace(/_/g, '-').replace(/[^A-Za-z0-9-]/g, '')
  if (!ascii) return 'en'
  const parts = ascii.split('-').filter(Boolean)
  const [first, ...rest] = parts
  if (!first) return 'en'
  const primary = first.toLowerCase()
  const suffix = rest.map((p) => (p.length === 2 ? p.toUpperCase() : p.toLowerCase()))
  return [primary, ...suffix].join('-').slice(0, 16)
}

/** Replace `{name}` placeholders in a storefront message template. */
export function formatStorefrontMessage(
  template: string,
  params: Record<string, string | number | null | undefined>,
): string {
  return Object.entries(params).reduce((out, [key, value]) => {
    if (value == null || value === '') return out
    return out.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value))
  }, template)
}

/** BCP 47 tag for `toLocaleDateString` / similar APIs. */
export function shopLocaleToBcp47(locale: string | null | undefined): string {
  const n = normalizeShopLocale(locale)
  if (n === 'zh-Hant' || n === 'zh-CN') return n
  if (n === 'en') return 'en'
  return n || 'en'
}

/** UI strings for shop chrome; page titles come from the CMS. */
export const shopStorefrontMessages: Record<string, Record<string, string>> = {
  en: {
    'header.language': 'Language',
    'header.login': 'Login',
    'header.myAccount': 'My account',
    'header.cart': 'Cart',
    'header.signOut': 'Sign out',
    'header.openAccountMenu': 'Open account menu',
    'header.account': 'Account',
    'header.openMenu': 'Open menu',
    'header.closeMenu': 'Close menu',
    'catalog.search': 'Search',
    'catalog.searchPlaceholder': 'Name, SKU, description…',
    'catalog.group': 'Group',
    'catalog.allGroups': 'All groups',
    'catalog.groupOthers': 'Others',
    'catalog.groupsSidebar': 'Groups',
    'catalog.brand': 'Publisher / brand',
    'catalog.allBrands': 'All',
    'catalog.brandOther': '{name} (other)',
    'catalog.clearFilters': 'Clear filters',
    'catalog.showingRange': 'Showing {start}–{end} of {total} {unit}',
    'catalog.searchFor': 'for "{query}"',
    'catalog.product': 'product',
    'catalog.products': 'products',
    'catalog.pageOf': 'Page {page} of {pages}',
    'catalog.noProductsMatch': 'No products match your filters.',
    'catalog.previous': 'Previous',
    'catalog.next': 'Next',
    'catalog.page': 'Page',
    'product.outOfStock': 'Out of Stock',
    'product.chooseOptions': 'Choose options',
    'product.addToCart': 'Add to Cart',
    'product.addingToCart': 'Adding…',
    'product.viewProduct': 'View product',
    'product.contactToBuy': 'Contact to Buy',
    'product.contactComingSoon': 'Contact options coming soon.',
    'product.displayOnly': 'Display Only',
    'product.promotion': 'Promotion',
    'product.original': 'Original',
    'product.from': 'From',
    'product.backToProducts': 'Back to products',
    'product.notFound': 'Product not found',
    'product.notFoundHint': 'This item may be unavailable or the link is incorrect.',
    'product.addFavorite': 'Add to favorites',
    'product.removeFavorite': 'Remove from favorites',
    'product.favoriteAdded': 'Added to favorites',
    'product.favoriteRemoved': 'Removed from favorites',
    'product.favoriteFailed': 'Failed to update favorite',
    'event.viewDetails': 'View details →',
    'event.registerNow': 'Register now →',
    'event.originalPrice': 'Was',
    'event.backToEvents': 'Back to events',
    'event.location': 'Location',
    'event.showMap': 'Show map',
    'event.mapTitle': 'Event location map',
    'event.mapPrivacyNote': 'Loads Google Maps only when you click. Google may receive your IP address.',
    'event.openInGoogleMaps': 'Open in Google Maps',
    'event.howToGetThere': 'How do you want to get there?',
    'event.travelDriving': 'Driving',
    'event.travelTransit': 'Public transport',
    'event.travelCycling': 'Cycling',
    'event.travelWalking': 'Walking',
    'event.upcomingHeading': 'Upcoming Events',
    'event.pastHeading': 'Past Events',
    'event.allHeading': 'Events',
    'event.none': 'No events at the moment.',
    'event.noneToShow': 'No events to show right now.',
    'event.noneMatchFilters': 'No events match your search or date.',
    'event.search': 'Search events',
    'event.searchPlaceholder': 'Artist, show, venue…',
    'event.dateFilter': 'Date',
    'event.clearFilters': 'Clear',
    'event.filterResultCount': '{count} events found',
    'event.seatMapAria': 'Theater seating plan',
    'event.seatMapStage': 'STAGE',
    'event.seatMapHint': 'Click an available seat to add it. Click again to remove. Colours show ticket tranches.',
    'event.seatUnavailable': 'Taken / blocked',
    'event.seatSelected': 'Selected',
    'event.seatAlreadyInOrder': 'This seat is already in your order',
    'event.selectSeatsOnMap': 'Select seats on the map',
    'event.viewAll': 'View all events →',
    'event.viewAllShort': 'View all →',
    'event.notFound': 'Event not found',
    'event.notFoundHint': 'This event may be unavailable or the link is incorrect.',
    'event.eventsNav': 'Events',
    'event.backToEvent': '← Back to event',
    'event.buyTickets': 'Buy tickets',
    'event.ticketsClosed': 'This event has ended. Tickets are no longer available.',
    'event.ticketsNotSoldOnline': 'Tickets are not sold online for this event.',
    'event.description': 'Description',
    'blog.readMore': 'Read more →',
    'blog.back': '← Back',
    'blog.backToHome': 'Back to home',
    'blog.postNotFound': 'Post not found.',
    'blog.category.news': 'News',
    'blog.category.promotion': 'Promotion',
    'blog.category.schedule': 'Schedule',
    'blog.category.announcement': 'Announcement',
    'common.welcome': 'Welcome',
    'common.welcomeToShop': 'Welcome to {name}',
    'common.discoverProductsEvents': 'Discover our products and events.',
    'common.browseProducts': 'Browse Products',
    'common.viewAllProducts': 'View All Products',
    'common.pageNotAvailable': 'This page is not available.',
    'common.backToShop': 'Back to shop',
    'common.imageNotSet': 'Image column (no URL set)',
    'common.whatsappNotSet':
      'WhatsApp link is not set for this shop. Add it under Dashboard → Website (same field as the footer).',
    'common.whatsappNotSetTitle': 'Set WhatsApp under Dashboard → Website → Contact / WhatsApp link',
    'form.thankYou': 'Thank you — your message has been received.',
    'form.yourName': 'Your name',
    'form.email': 'Email',
    'form.submit': 'Submit',
    'form.sending': 'Sending…',
    'form.notFound': 'Form not found or not published.',
    'reviews.title': 'Customer reviews',
    'reviews.none': 'No reviews yet — be the first.',
    'reviews.summary': '{avg} / 5 · {count} {unit}',
    'reviews.review': 'review',
    'reviews.reviews': 'reviews',
    'reviews.write': 'Write a review',
    'reviews.rating': 'Rating',
    'reviews.stars': '{n} stars',
    'reviews.bodyPlaceholder': 'Your experience (optional)',
    'reviews.submit': 'Submit review',
    'reviews.submitting': 'Submitting…',
    'reviews.moderatedNote': 'Reviews are moderated before they appear.',
    'reviews.nameRequired': 'Please enter your name',
    'reviews.submitSuccess': 'Review submitted',
    'reviews.submitFailed': 'Could not submit review',
    'cart.title': 'Shopping Cart',
    'cart.empty': 'Your cart is empty.',
    'cart.continueShopping': 'Continue Shopping',
    'cart.continueShoppingBack': '← Continue Shopping',
    'cart.priceEach': '{price} each',
    'cart.remove': 'Remove',
    'cart.subtotal': 'Subtotal',
    'cart.bundle': 'Bundle: {name}',
    'cart.orderTotal': 'Order total',
    'cart.proceedToCheckout': 'Proceed to checkout',
    'cart.signInToCheckout': 'Sign in to checkout',
    'cart.checkoutUnavailable': 'Online checkout is not available for this store.',
    'cart.contactToComplete': 'Contact the store to complete your order',
    'cart.updateQtyFailed': 'Could not update quantity',
    'checkout.progressAria': 'Checkout progress',
    'checkout.stepCart': 'Cart',
    'checkout.stepShipping': 'Shipping',
    'checkout.stepPayment': 'Payment',
    'checkout.stepConfirmation': 'Confirmation',
    'checkout.titleShipping': 'Shipping',
    'checkout.titlePayment': 'Payment',
    'checkout.delivery': 'Delivery',
    'checkout.shipToAddress': 'Ship to address',
    'checkout.selfPickup': 'Self pickup',
    'checkout.mixedPickupShipHint':
      'Self pickup is available when every item supports pickup. If you choose shipping, pickup-only items can also be shipped by courier.',
    'checkout.pickupLocation': 'Pickup location',
    'checkout.deliveryInstructions': 'Delivery instructions (optional)',
    'checkout.noShippingRequired': 'No shipping details required for this order.',
    'checkout.backToCart': 'Back to cart',
    'checkout.backToCartArrow': '← Back to cart',
    'checkout.continueToPayment': 'Continue to payment',
    'checkout.orderSummary': 'Order summary',
    'checkout.bundleSavings': 'Bundle savings',
    'checkout.couponApplied': 'Coupon ({code})',
    'checkout.total': 'Total',
    'checkout.couponCode': 'Coupon code',
    'checkout.couponPlaceholder': 'Enter code',
    'checkout.couponApply': 'Apply',
    'checkout.couponChecking': 'Checking…',
    'checkout.couponAppliedMsg': 'Coupon applied',
    'checkout.couponFailed': 'Coupon could not be applied',
    'checkout.paymentMethod': 'Payment method',
    'checkout.payWith': 'Pay with {name}',
    'checkout.payWithTest': '{name} (test)',
    'checkout.payWithDemo': '{name} (demo — no real charge)',
    'checkout.checkoutUnavailable': 'Online checkout is not available.',
    'checkout.backToShipping': '← Back to shipping',
    'checkout.pleaseWait': 'Please wait…',
    'checkout.placeOrderFps': 'Place order & pay with FPS',
    'checkout.paySecurely': 'Pay securely with {name}',
    'checkout.contactStore': 'Contact store',
    'checkout.creatingOrder': 'Creating your order…',
    'checkout.connectingPayment': 'Connecting to secure payment…',
    'checkout.doNotClose': 'Please do not close this window.',
    'checkout.overlayDefault': 'Preparing secure payment…',
    'checkout.uatTitle': 'Test mode — UAT only',
    'checkout.uatBody':
      'This shop is using a KPay sandbox (UAT) environment. Payments here are for testing only — no real charges will be made.',
    'checkout.dismiss': 'Dismiss',
    'checkout.paymentRedirectMissing': 'Payment redirect was not returned',
    'checkout.validation.pickupLocation': 'Please select a pickup location',
    'checkout.validation.shippingAddress': 'Please enter a shipping address',
    'checkout.validation.addressLine1': 'Please enter address line 1',
    'checkout.validation.city': 'Please enter city',
    'checkout.validation.country': 'Please select country',
    'checkout.validation.noCountries': 'This shop is not accepting shipments to any country',
    'checkout.validation.countryNotAllowed': 'We do not ship to the selected country',
    'checkout.validation.phoneAreaCode': 'Select an area code',
    'checkout.validation.phoneInvalid':
      'Enter a valid phone number: use international format (+country code and number, up to 15 digits) or 4–15 digits for a local number',
    'checkout.address.recipientName': 'Recipient name',
    'checkout.address.fullName': 'Full name',
    'checkout.address.phone': 'Phone',
    'checkout.address.phonePlaceholder': 'e.g. 91234567',
    'checkout.address.line1': 'Address line 1',
    'checkout.address.line1Placeholder': 'Street address, P.O. box, etc.',
    'checkout.address.line2': 'Address line 2',
    'checkout.address.line2Placeholder': 'Apartment, suite, unit, building, floor, etc.',
    'checkout.address.city': 'City',
    'checkout.address.stateProvince': 'State / province',
    'checkout.address.statePlaceholder': 'State or province',
    'checkout.address.postalCode': 'Postal / ZIP code',
    'checkout.address.postalPlaceholder': 'Postal code',
    'checkout.address.country': 'Country',
    'checkout.address.selectCountry': 'Select country',
    'checkout.error.cart_empty.title': 'Your cart is empty',
    'checkout.error.cart_empty.message': 'Add items to your cart before checking out.',
    'checkout.error.cart_session_required.title': 'Cart session expired',
    'checkout.error.cart_session_required.message': 'Refresh this page and try again.',
    'checkout.error.cart_outdated.title': 'Cart needs review',
    'checkout.error.cart_outdated.message':
      'Open your cart to see updated options and prices, then try checkout again.',
    'checkout.error.payment_unavailable.title': 'Payment unavailable',
    'checkout.error.payment_unavailable.message':
      'This shop is not accepting online payments right now.',
    'checkout.error.payment_not_configured.title': 'Payment not set up',
    'checkout.error.payment_not_configured.message': 'Please contact the store for assistance.',
    'checkout.error.payment_config.title': 'Payment could not be started',
    'checkout.error.payment_config.message':
      'Your items are still in your cart. Please try again shortly or contact the store if the problem continues.',
    'checkout.error.payment_gateway.title': 'Payment provider error',
    'checkout.error.payment_gateway.message':
      'Your items are still in your cart. Please try again or contact the store for another way to pay.',
    'checkout.error.order_invalid.title': 'Order could not be prepared',
    'checkout.error.order_invalid.message': 'Review your cart and try again.',
    'checkout.error.shop_not_found.title': 'Shop not found',
    'checkout.error.shop_not_found.message': 'The shop you are looking for could not be found.',
    'checkout.error.login_required.title': 'Sign in required',
    'checkout.error.login_required.message':
      'Please sign in to continue checkout. Your cart will be saved.',
    'checkout.error.server_error.title': 'Checkout failed',
    'checkout.error.server_error.message':
      'Something went wrong. Your items are still in your cart — please try again in a moment.',
    'checkout.error.network.title': 'Connection problem',
    'checkout.error.network.message':
      'We could not reach the store. Check your internet connection and try again. Your cart has been saved.',
    'checkout.confirm.title': 'Order confirmed',
    'checkout.confirm.thankYou': 'Thank you for your order.',
    'checkout.confirm.orderLabel': 'Order {code}',
    'checkout.confirm.total': 'Total: {currency} ${amount}',
    'checkout.confirm.paymentReceived': 'Payment received — thank you!',
    'checkout.confirm.completePayment': 'Complete payment below to confirm your order.',
    'checkout.confirm.continueShopping': 'Continue shopping',
    'checkout.confirm.viewCart': 'View cart',
    'checkout.confirm.invalidOrder': 'Invalid order reference.',
    'checkout.confirm.returnToCart': 'Return to cart',
    'checkout.fps.preparing': 'Preparing FPS payment…',
    'checkout.fps.title': 'Pay with FPS (轉數快)',
    'checkout.fps.scanPay': 'Scan to pay HK${amount}. Reference: {code}',
    'checkout.fps.manualPay':
      'Transfer HK${amount} via FPS using the bank details below. Put {code} in the payment reference / remark.',
    'checkout.fps.qrAlt': 'FPS payment QR code',
    'checkout.fps.manualTransfer': 'FPS transfer details',
    'checkout.fps.bankCode': 'Bank code',
    'checkout.fps.accountNumber': 'Account number',
    'checkout.fps.amountLabel': 'Amount',
    'checkout.fps.referenceLabel': 'Reference',
    'checkout.fps.afterPay':
      'FPS does not confirm payment automatically. After you pay, the store will mark the order paid. Keep your order reference handy if you need support.',
    'checkout.fps.unavailableTitle': 'FPS payment not available',
    'checkout.fps.unavailableBody':
      'FPS may not be configured for this store, or this order has no outstanding balance.',
    'checkout.return.confirming': 'Confirming your payment…',
    'checkout.return.missingOrder':
      'We could not find your order reference. If you completed payment, contact the store with your receipt.',
    'checkout.return.timeout':
      'Payment confirmation is taking longer than expected. Please tap Check payment again in a moment.',
    'checkout.return.statusUnavailableMsg': 'We could not confirm your payment status right now.',
    'checkout.return.statusUnavailableTitle': 'Payment status unavailable',
    'checkout.return.checkAgain': 'Check payment again',
    'checkout.return.checking': 'Checking payment…',
    'checkout.return.backToShop': 'Back to shop',
    'checkout.return.thankYouTickets': 'Thank you — your tickets are ready!',
    'checkout.return.thankYouOrder': 'Thank you for your order!',
    'checkout.return.orderPaid': 'Order {code} has been paid',
    'checkout.return.orderPaidAmount': 'Order {code} has been paid ({currency} {amount}).',
    'checkout.return.ticketsHint':
      'Download or print your tickets below. You can also show the QR code at the door.',
    'checkout.return.processSoon': 'We will process your order shortly.',
    'checkout.return.orderDetails': 'Order details',
    'checkout.return.yourTickets': 'Your tickets',
    'checkout.return.qty': 'Qty {qty}',
    'checkout.return.lineTotal': '{currency} {amount}',
    'checkout.return.orderTotal': 'Total',
    'checkout.return.downloadTicket': 'Download PDF',
    'checkout.return.ticket': 'Ticket',
    'checkout.return.ticketSeat': 'Ticket — {seat}',
    'checkout.return.ticketMeta': '{event} · {tier}',
    'checkout.return.ticketMetaEvent': '{event}',
    'checkout.return.continueShoppingSecondary': 'Continue shopping',
    'checkout.return.paymentFailedTitle': 'Payment failed',
    'checkout.return.paymentFailedBody': 'Order {code} could not be paid.',
    'checkout.return.tryAgainHint':
      'You can return to your cart and try again with a different payment method.',
    'checkout.return.notCompletedTitle': 'Payment not completed',
    'checkout.return.processingTitle': 'Payment processing',
    'checkout.return.notCompletedBody':
      'Order {code} has not been marked as paid yet. If you already completed payment with the payment provider, tap Check payment again below. If you cancelled on the payment page, you can return to your cart and try again.',
    'checkout.return.processingBody':
      'Order {code} is being confirmed. If you completed payment, it may take a moment to update — you can refresh this page shortly.',
    'checkout.return.refreshStatus': 'Refresh status',
    'support.needHelp': 'Need help?',
    'support.close': 'Close',
    'support.title': 'Support',
    'support.hoursHint': 'We usually reply during shop hours',
    'support.subjectPlaceholder': 'Subject (optional)',
    'support.bodyPlaceholder': 'How can we help?',
    'support.back': 'Back',
    'support.send': 'Send',
    'support.sending': 'Sending…',
    'support.allChats': 'All chats',
    'support.new': 'New',
    'support.newConversation': 'New conversation',
    'support.noConversations': 'No conversations yet.',
    'support.askQuestion': 'Ask a question',
    'support.conversation': 'Conversation',
    'support.typeMessage': 'Type a message…',
    'support.replyToReopen': 'Reply to reopen this conversation…',
    'support.attachHint': 'Attach screenshots, PDF, Excel, or Word (up to {{max}} files, 10MB each)',
    'support.attachFiles': 'Attach files',
    'support.noMessages': 'No messages yet.',
    'support.sendFailed': 'Failed to send message',
    'support.maxFiles': 'Maximum {{max}} files per message',
    'support.fileTooLarge': '{{name}} is larger than 10MB',
    'demopay.title': 'Demo payment',
    'demopay.bannerTitle': 'Demo payment — no real charge',
    'demopay.bannerBody':
      'This is a simulated payment for demonstration. Pay marks the order paid; Cancel leaves it unpaid.',
    'demopay.orderLabel': 'Order {code}',
    'demopay.amountHint': 'Amount due (simulated)',
    'demopay.pay': 'Pay (simulate success)',
    'demopay.cancel': 'Cancel (simulate failure)',
    'demopay.working': 'Please wait…',
    'demopay.missingSession': 'This demo payment link is missing or invalid.',
    'demopay.loadFailed': 'Could not load the demo payment session.',
    'demopay.payFailed': 'Could not complete the demo payment.',
    'demopay.cancelFailed': 'Could not cancel the demo payment.',
    'demopay.backToShop': 'Back to shop',
  },
  'zh-Hant': {
    'header.language': '語言',
    'header.login': '登入',
    'header.myAccount': '我的帳戶',
    'header.cart': '購物車',
    'header.signOut': '登出',
    'header.openAccountMenu': '開啟帳戶選單',
    'header.account': '帳戶',
    'header.openMenu': '開啟選單',
    'header.closeMenu': '關閉選單',
    'catalog.search': '搜尋',
    'catalog.searchPlaceholder': '名稱、SKU、描述…',
    'catalog.group': '分組',
    'catalog.allGroups': '全部分組',
    'catalog.groupOthers': '其他',
    'catalog.groupsSidebar': '分組',
    'catalog.brand': '出版社／品牌',
    'catalog.allBrands': '全部',
    'catalog.brandOther': '{name}（其他）',
    'catalog.clearFilters': '清除篩選',
    'catalog.showingRange': '顯示第 {start}–{end} 件，共 {total} 件產品',
    'catalog.searchFor': '搜尋「{query}」',
    'catalog.product': '件產品',
    'catalog.products': '件產品',
    'catalog.pageOf': '第 {page} 頁，共 {pages} 頁',
    'catalog.noProductsMatch': '沒有符合篩選條件的產品。',
    'catalog.previous': '上一頁',
    'catalog.next': '下一頁',
    'catalog.page': '第',
    'product.outOfStock': '缺貨',
    'product.chooseOptions': '選擇選項',
    'product.addToCart': '加入購物車',
    'product.addingToCart': '加入中…',
    'product.viewProduct': '查看產品',
    'product.contactToBuy': '聯絡購買',
    'product.contactComingSoon': '聯絡方式即將推出。',
    'product.displayOnly': '僅供展示',
    'product.promotion': '促銷價',
    'product.original': '原價',
    'product.from': '由',
    'product.backToProducts': '返回產品列表',
    'product.notFound': '找不到產品',
    'product.notFoundHint': '此商品可能已下架，或連結不正確。',
    'product.addFavorite': '加入收藏',
    'product.removeFavorite': '取消收藏',
    'product.favoriteAdded': '已加入收藏',
    'product.favoriteRemoved': '已取消收藏',
    'product.favoriteFailed': '更新收藏失敗',
    'event.viewDetails': '查看詳情 →',
    'event.registerNow': '立即報名 →',
    'event.originalPrice': '原價',
    'event.backToEvents': '返回活動列表',
    'event.location': '地點',
    'event.showMap': '顯示地圖',
    'event.mapTitle': '活動地點地圖',
    'event.mapPrivacyNote': '僅在您點擊後載入 Google 地圖。Google 可能會收到您的 IP 位址。',
    'event.openInGoogleMaps': '在 Google 地圖中開啟',
    'event.howToGetThere': '如何前往？',
    'event.travelDriving': '駕車',
    'event.travelTransit': '公共交通',
    'event.travelCycling': '單車',
    'event.travelWalking': '步行',
    'event.upcomingHeading': '即將舉行的活動',
    'event.pastHeading': '過往活動',
    'event.allHeading': '活動',
    'event.none': '目前沒有活動。',
    'event.noneToShow': '目前沒有可顯示的活動。',
    'event.noneMatchFilters': '沒有符合搜尋或日期的活動。',
    'event.search': '搜尋活動',
    'event.searchPlaceholder': '演出、活動、場地…',
    'event.dateFilter': '日期',
    'event.clearFilters': '清除',
    'event.filterResultCount': '找到 {count} 個活動',
    'event.seatMapAria': '劇場座位圖',
    'event.seatMapStage': '舞台',
    'event.seatMapHint': '點選可選座位加入訂單，再點一次移除。顏色代表不同票種區域。',
    'event.seatUnavailable': '已售／停售',
    'event.seatSelected': '已選',
    'event.seatAlreadyInOrder': '此座位已在您的訂單中',
    'event.selectSeatsOnMap': '在座位圖上選座',
    'event.viewAll': '查看所有活動 →',
    'event.viewAllShort': '查看全部 →',
    'event.notFound': '找不到活動',
    'event.notFoundHint': '此活動可能已下架，或連結不正確。',
    'event.eventsNav': '活動',
    'event.backToEvent': '← 返回活動',
    'event.buyTickets': '購買門票',
    'event.ticketsClosed': '此活動已結束，無法再購買門票。',
    'event.ticketsNotSoldOnline': '此活動不提供線上購票。',
    'event.description': '簡介',
    'blog.readMore': '閱讀更多 →',
    'blog.back': '← 返回',
    'blog.backToHome': '返回首頁',
    'blog.postNotFound': '找不到文章。',
    'blog.category.news': '新聞',
    'blog.category.promotion': '優惠',
    'blog.category.schedule': '日程',
    'blog.category.announcement': '公告',
    'common.welcome': '歡迎',
    'common.welcomeToShop': '歡迎來到 {name}',
    'common.discoverProductsEvents': '探索我們的產品與活動。',
    'common.browseProducts': '瀏覽產品',
    'common.viewAllProducts': '查看所有產品',
    'common.pageNotAvailable': '此頁面目前無法使用。',
    'common.backToShop': '返回商店',
    'common.imageNotSet': '圖片欄位（尚未設定網址）',
    'common.whatsappNotSet':
      '此商店尚未設定 WhatsApp 連結。請到 Dashboard → Website（與頁尾相同欄位）新增。',
    'common.whatsappNotSetTitle': '請到 Dashboard → Website → Contact / WhatsApp 連結設定 WhatsApp',
    'form.thankYou': '感謝您 — 我們已收到您的訊息。',
    'form.yourName': '您的姓名',
    'form.email': '電郵',
    'form.submit': '提交',
    'form.sending': '傳送中…',
    'form.notFound': '找不到表單或尚未發佈。',
    'reviews.title': '顧客評價',
    'reviews.none': '尚無評價 — 成為第一位評價者。',
    'reviews.summary': '{avg} / 5 · {count} 則{unit}',
    'reviews.review': '評價',
    'reviews.reviews': '評價',
    'reviews.write': '撰寫評價',
    'reviews.rating': '評分',
    'reviews.stars': '{n} 星',
    'reviews.bodyPlaceholder': '您的體驗（選填）',
    'reviews.submit': '提交評價',
    'reviews.submitting': '提交中…',
    'reviews.moderatedNote': '評價經審核後才會顯示。',
    'reviews.nameRequired': '請輸入您的姓名',
    'reviews.submitSuccess': '評價已提交',
    'reviews.submitFailed': '無法提交評價',
    'cart.title': '購物車',
    'cart.empty': '您的購物車是空的。',
    'cart.continueShopping': '繼續購物',
    'cart.continueShoppingBack': '← 繼續購物',
    'cart.priceEach': '每件 {price}',
    'cart.remove': '移除',
    'cart.subtotal': '小計',
    'cart.bundle': '組合優惠：{name}',
    'cart.orderTotal': '訂單總額',
    'cart.proceedToCheckout': '前往結帳',
    'cart.signInToCheckout': '登入後結帳',
    'cart.checkoutUnavailable': '此商店目前無法使用線上結帳。',
    'cart.contactToComplete': '請聯絡商店以完成訂單',
    'cart.updateQtyFailed': '無法更新數量',
    'checkout.progressAria': '結帳進度',
    'checkout.stepCart': '購物車',
    'checkout.stepShipping': '運送',
    'checkout.stepPayment': '付款',
    'checkout.stepConfirmation': '確認',
    'checkout.titleShipping': '運送',
    'checkout.titlePayment': '付款',
    'checkout.delivery': '配送方式',
    'checkout.shipToAddress': '送貨上門',
    'checkout.selfPickup': '自行取貨',
    'checkout.mixedPickupShipHint':
      '當所有商品均支援取貨時可選擇自行取貨。若選擇送貨，僅支援取貨的商品亦可由速遞運送。',
    'checkout.pickupLocation': '取貨地點',
    'checkout.deliveryInstructions': '配送說明（選填）',
    'checkout.noShippingRequired': '此訂單無需填寫運送資料。',
    'checkout.backToCart': '返回購物車',
    'checkout.backToCartArrow': '← 返回購物車',
    'checkout.continueToPayment': '繼續至付款',
    'checkout.orderSummary': '訂單摘要',
    'checkout.bundleSavings': '組合優惠',
    'checkout.couponApplied': '優惠碼（{code}）',
    'checkout.total': '總額',
    'checkout.couponCode': '優惠碼',
    'checkout.couponPlaceholder': '輸入優惠碼',
    'checkout.couponApply': '套用',
    'checkout.couponChecking': '檢查中…',
    'checkout.couponAppliedMsg': '已套用優惠碼',
    'checkout.couponFailed': '無法套用優惠碼',
    'checkout.paymentMethod': '付款方式',
    'checkout.payWith': '使用 {name} 付款',
    'checkout.payWithTest': '{name}（測試）',
    'checkout.payWithDemo': '{name}（示範 — 不會真實扣款）',
    'checkout.checkoutUnavailable': '目前無法使用線上結帳。',
    'checkout.backToShipping': '← 返回運送',
    'checkout.pleaseWait': '請稍候…',
    'checkout.placeOrderFps': '下單並以轉數快付款',
    'checkout.paySecurely': '以 {name} 安全付款',
    'checkout.contactStore': '聯絡商店',
    'checkout.creatingOrder': '正在建立訂單…',
    'checkout.connectingPayment': '正在連接安全付款…',
    'checkout.doNotClose': '請勿關閉此視窗。',
    'checkout.overlayDefault': '正在準備安全付款…',
    'checkout.uatTitle': '測試模式 — 僅限 UAT',
    'checkout.uatBody':
      '此商店正使用 KPay 沙盒（UAT）環境。此處付款僅供測試，不會產生真實收費。',
    'checkout.dismiss': '關閉',
    'checkout.paymentRedirectMissing': '未取得付款重新導向',
    'checkout.validation.pickupLocation': '請選擇取貨地點',
    'checkout.validation.shippingAddress': '請輸入運送地址',
    'checkout.validation.addressLine1': '請輸入地址第一行',
    'checkout.validation.city': '請輸入城市',
    'checkout.validation.country': '請選擇國家／地區',
    'checkout.validation.noCountries': '此商店目前不接受任何國家／地區的送貨',
    'checkout.validation.countryNotAllowed': '我們不運送至所選國家／地區',
    'checkout.validation.phoneAreaCode': '請選擇區號',
    'checkout.validation.phoneInvalid':
      '請輸入有效電話號碼：使用國際格式（+國家代碼及號碼，最多 15 位數字）或 4–15 位本地號碼',
    'checkout.address.recipientName': '收件人姓名',
    'checkout.address.fullName': '全名',
    'checkout.address.phone': '電話',
    'checkout.address.phonePlaceholder': '例如 91234567',
    'checkout.address.line1': '地址第一行',
    'checkout.address.line1Placeholder': '街道地址、郵政信箱等',
    'checkout.address.line2': '地址第二行',
    'checkout.address.line2Placeholder': '公寓、套房、單位、大廈、樓層等',
    'checkout.address.city': '城市',
    'checkout.address.stateProvince': '州／省',
    'checkout.address.statePlaceholder': '州或省',
    'checkout.address.postalCode': '郵遞區號',
    'checkout.address.postalPlaceholder': '郵遞區號',
    'checkout.address.country': '國家／地區',
    'checkout.address.selectCountry': '選擇國家／地區',
    'checkout.error.cart_empty.title': '購物車是空的',
    'checkout.error.cart_empty.message': '結帳前請先將商品加入購物車。',
    'checkout.error.cart_session_required.title': '購物車工作階段已過期',
    'checkout.error.cart_session_required.message': '請重新整理此頁面後再試。',
    'checkout.error.cart_outdated.title': '購物車需要重新確認',
    'checkout.error.cart_outdated.message': '請開啟購物車查看更新後的選項與價格，然後再試一次結帳。',
    'checkout.error.payment_unavailable.title': '無法付款',
    'checkout.error.payment_unavailable.message': '此商店目前不接受線上付款。',
    'checkout.error.payment_not_configured.title': '尚未設定付款',
    'checkout.error.payment_not_configured.message': '請聯絡商店以取得協助。',
    'checkout.error.payment_config.title': '無法開始付款',
    'checkout.error.payment_config.message':
      '您的商品仍在購物車中。請稍後再試，或聯絡商店尋求協助。',
    'checkout.error.payment_gateway.title': '付款服務商錯誤',
    'checkout.error.payment_gateway.message':
      '您的商品仍在購物車中。請再試一次，或聯絡商店以其他方式付款。',
    'checkout.error.order_invalid.title': '無法準備訂單',
    'checkout.error.order_invalid.message': '請檢查購物車後再試。',
    'checkout.error.shop_not_found.title': '找不到商店',
    'checkout.error.shop_not_found.message': '找不到您要尋找的商店。',
    'checkout.error.login_required.title': '需要登入',
    'checkout.error.login_required.message': '請先登入以繼續結帳。您的購物車會保留。',
    'checkout.error.server_error.title': '結帳失敗',
    'checkout.error.server_error.message': '發生錯誤。您的商品仍在購物車中 — 請稍後再試。',
    'checkout.error.network.title': '連線問題',
    'checkout.error.network.message':
      '無法連接到商店。請檢查網路連線後再試。您的購物車已儲存。',
    'checkout.confirm.title': '訂單已確認',
    'checkout.confirm.thankYou': '感謝您的訂購。',
    'checkout.confirm.orderLabel': '訂單 {code}',
    'checkout.confirm.total': '總額：{currency} ${amount}',
    'checkout.confirm.paymentReceived': '已收到付款 — 謝謝！',
    'checkout.confirm.completePayment': '請在下方完成付款以確認訂單。',
    'checkout.confirm.continueShopping': '繼續購物',
    'checkout.confirm.viewCart': '查看購物車',
    'checkout.confirm.invalidOrder': '無效的訂單編號。',
    'checkout.confirm.returnToCart': '返回購物車',
    'checkout.fps.preparing': '正在準備轉數快付款…',
    'checkout.fps.title': '以轉數快（FPS）付款',
    'checkout.fps.scanPay': '掃碼支付 HK${amount}。參考編號：{code}',
    'checkout.fps.manualPay': '請以轉數快轉帳 HK${amount}。在備註／參考編號填寫 {code}。',
    'checkout.fps.qrAlt': '轉數快付款二維碼',
    'checkout.fps.manualTransfer': '轉數快轉帳資料',
    'checkout.fps.bankCode': '銀行代碼',
    'checkout.fps.accountNumber': '戶口號碼',
    'checkout.fps.amountLabel': '金額',
    'checkout.fps.referenceLabel': '參考編號',
    'checkout.fps.afterPay': '轉數快不會自動確認付款。付款後，商店會將訂單標示為已付款。如需支援，請保留訂單參考編號。',
    'checkout.fps.unavailableTitle': '無法使用轉數快付款',
    'checkout.fps.unavailableBody': '此商店可能尚未設定轉數快，或此訂單沒有未付餘額。',
    'checkout.return.confirming': '正在確認您的付款…',
    'checkout.return.missingOrder':
      '找不到您的訂單編號。若您已完成付款，請攜帶收據聯絡商店。',
    'checkout.return.timeout': '付款確認時間較長。請稍後再按「再次檢查付款」。',
    'checkout.return.statusUnavailableMsg': '目前無法確認您的付款狀態。',
    'checkout.return.statusUnavailableTitle': '無法取得付款狀態',
    'checkout.return.checkAgain': '再次檢查付款',
    'checkout.return.checking': '正在檢查付款…',
    'checkout.return.backToShop': '返回商店',
    'checkout.return.thankYouTickets': '謝謝 — 您的門票已準備好！',
    'checkout.return.thankYouOrder': '感謝您的訂購！',
    'checkout.return.orderPaid': '訂單 {code} 已付款',
    'checkout.return.orderPaidAmount': '訂單 {code} 已付款（{currency} {amount}）。',
    'checkout.return.ticketsHint': '請下載或列印下方門票。入場時亦可出示二維碼。',
    'checkout.return.processSoon': '我們將盡快處理您的訂單。',
    'checkout.return.orderDetails': '訂單詳情',
    'checkout.return.yourTickets': '您的門票',
    'checkout.return.qty': '數量 {qty}',
    'checkout.return.lineTotal': '{currency} {amount}',
    'checkout.return.orderTotal': '合計',
    'checkout.return.downloadTicket': '下載 PDF',
    'checkout.return.ticket': '門票',
    'checkout.return.ticketSeat': '門票 — {seat}',
    'checkout.return.ticketMeta': '{event} · {tier}',
    'checkout.return.ticketMetaEvent': '{event}',
    'checkout.return.continueShoppingSecondary': '繼續購物',
    'checkout.return.paymentFailedTitle': '付款失敗',
    'checkout.return.paymentFailedBody': '訂單 {code} 未能完成付款。',
    'checkout.return.tryAgainHint': '您可以返回購物車，改用其他付款方式再試。',
    'checkout.return.notCompletedTitle': '尚未完成付款',
    'checkout.return.processingTitle': '付款處理中',
    'checkout.return.notCompletedBody':
      '訂單 {code} 尚未標記為已付款。若您已在付款服務商完成付款，請按下方「再次檢查付款」。若您在付款頁取消，可返回購物車再試。',
    'checkout.return.processingBody':
      '訂單 {code} 正在確認中。若您已完成付款，更新可能需要一點時間 — 您稍後可重新整理此頁。',
    'checkout.return.refreshStatus': '重新整理狀態',
    'support.needHelp': '需要協助？',
    'support.close': '關閉',
    'support.title': '客戶支援',
    'support.hoursHint': '我們通常在營業時間回覆',
    'support.subjectPlaceholder': '主旨（選填）',
    'support.bodyPlaceholder': '我們可以如何協助您？',
    'support.back': '返回',
    'support.send': '傳送',
    'support.sending': '傳送中…',
    'support.allChats': '所有對話',
    'support.new': '新增',
    'support.newConversation': '新對話',
    'support.noConversations': '尚無對話。',
    'support.askQuestion': '提出問題',
    'support.conversation': '對話',
    'support.typeMessage': '輸入訊息…',
    'support.replyToReopen': '回覆以重新開啟此對話…',
    'support.attachHint': '可附加截圖、PDF、Excel 或 Word（最多 {{max}} 個檔案，每個 10MB）',
    'support.attachFiles': '附加檔案',
    'support.noMessages': '尚無訊息。',
    'support.sendFailed': '傳送失敗',
    'support.maxFiles': '每則訊息最多 {{max}} 個檔案',
    'support.fileTooLarge': '{{name}} 超過 10MB',
    'demopay.title': '示範付款',
    'demopay.bannerTitle': '示範付款 — 不會真實扣款',
    'demopay.bannerBody': '此為示範用模擬付款。按「付款」會標記訂單已付；按「取消」則模擬付款失敗。',
    'demopay.orderLabel': '訂單 {code}',
    'demopay.amountHint': '應付款項（模擬）',
    'demopay.pay': '付款（模擬成功）',
    'demopay.cancel': '取消（模擬失敗）',
    'demopay.working': '請稍候…',
    'demopay.missingSession': '此示範付款連結無效或已遺失。',
    'demopay.loadFailed': '無法載入示範付款工作階段。',
    'demopay.payFailed': '無法完成示範付款。',
    'demopay.cancelFailed': '無法取消示範付款。',
    'demopay.backToShop': '返回商店',
  },
  'zh-CN': {
    'header.language': '语言',
    'header.login': '登录',
    'header.myAccount': '我的账户',
    'header.cart': '购物车',
    'header.signOut': '退出登录',
    'header.openAccountMenu': '打开账户菜单',
    'header.account': '账户',
    'header.openMenu': '打开菜单',
    'header.closeMenu': '关闭菜单',
    'catalog.search': '搜索',
    'catalog.searchPlaceholder': '名称、SKU、描述…',
    'catalog.group': '分组',
    'catalog.allGroups': '全部分组',
    'catalog.groupOthers': '其他',
    'catalog.groupsSidebar': '分组',
    'catalog.brand': '出版社／品牌',
    'catalog.allBrands': '全部',
    'catalog.brandOther': '{name}（其他）',
    'catalog.clearFilters': '清除筛选',
    'catalog.showingRange': '显示第 {start}–{end} 件，共 {total} 件产品',
    'catalog.searchFor': '搜索「{query}」',
    'catalog.product': '件产品',
    'catalog.products': '件产品',
    'catalog.pageOf': '第 {page} 页，共 {pages} 页',
    'catalog.noProductsMatch': '没有符合筛选条件的产品。',
    'catalog.previous': '上一页',
    'catalog.next': '下一页',
    'catalog.page': '第',
    'product.outOfStock': '缺货',
    'product.chooseOptions': '选择选项',
    'product.addToCart': '加入购物车',
    'product.addingToCart': '加入中…',
    'product.viewProduct': '查看产品',
    'product.contactToBuy': '联系购买',
    'product.contactComingSoon': '联系方式即将推出。',
    'product.displayOnly': '仅供展示',
    'product.promotion': '促销价',
    'product.original': '原价',
    'product.from': '从',
    'product.backToProducts': '返回产品列表',
    'product.notFound': '找不到产品',
    'product.notFoundHint': '此商品可能已下架，或链接不正确。',
    'product.addFavorite': '加入收藏',
    'product.removeFavorite': '取消收藏',
    'product.favoriteAdded': '已加入收藏',
    'product.favoriteRemoved': '已取消收藏',
    'product.favoriteFailed': '更新收藏失败',
    'event.viewDetails': '查看详情 →',
    'event.registerNow': '立即报名 →',
    'event.originalPrice': '原价',
    'event.backToEvents': '返回活动列表',
    'event.location': '地点',
    'event.showMap': '显示地图',
    'event.mapTitle': '活动地点地图',
    'event.mapPrivacyNote': '仅在您点击后加载 Google 地图。Google 可能会收到您的 IP 地址。',
    'event.openInGoogleMaps': '在 Google 地图中打开',
    'event.howToGetThere': '如何前往？',
    'event.travelDriving': '驾车',
    'event.travelTransit': '公共交通',
    'event.travelCycling': '骑行',
    'event.travelWalking': '步行',
    'event.upcomingHeading': '即将举行的活动',
    'event.pastHeading': '过往活动',
    'event.allHeading': '活动',
    'event.none': '目前没有活动。',
    'event.noneToShow': '目前没有可显示的活动。',
    'event.noneMatchFilters': '没有符合搜索或日期的活动。',
    'event.search': '搜索活动',
    'event.searchPlaceholder': '演出、活动、场地…',
    'event.dateFilter': '日期',
    'event.clearFilters': '清除',
    'event.filterResultCount': '找到 {count} 个活动',
    'event.seatMapAria': '剧场座位图',
    'event.seatMapStage': '舞台',
    'event.seatMapHint': '点击可选座位加入订单，再点一次移除。颜色代表不同票种区域。',
    'event.seatUnavailable': '已售／停售',
    'event.seatSelected': '已选',
    'event.seatAlreadyInOrder': '此座位已在您的订单中',
    'event.selectSeatsOnMap': '在座位图上选座',
    'event.viewAll': '查看所有活动 →',
    'event.viewAllShort': '查看全部 →',
    'event.notFound': '找不到活动',
    'event.notFoundHint': '此活动可能已下架，或链接不正确。',
    'event.eventsNav': '活动',
    'event.backToEvent': '← 返回活动',
    'event.buyTickets': '购买门票',
    'event.ticketsClosed': '此活动已结束，无法再购买门票。',
    'event.ticketsNotSoldOnline': '此活动不提供线上购票。',
    'event.description': '简介',
    'blog.readMore': '阅读更多 →',
    'blog.back': '← 返回',
    'blog.backToHome': '返回首页',
    'blog.postNotFound': '找不到文章。',
    'blog.category.news': '新闻',
    'blog.category.promotion': '优惠',
    'blog.category.schedule': '日程',
    'blog.category.announcement': '公告',
    'common.welcome': '欢迎',
    'common.welcomeToShop': '欢迎来到 {name}',
    'common.discoverProductsEvents': '探索我们的产品与活动。',
    'common.browseProducts': '浏览产品',
    'common.viewAllProducts': '查看所有产品',
    'common.pageNotAvailable': '此页面目前无法使用。',
    'common.backToShop': '返回商店',
    'common.imageNotSet': '图片栏位（尚未设置网址）',
    'common.whatsappNotSet':
      '此商店尚未设置 WhatsApp 链接。请到 Dashboard → Website（与页脚相同字段）添加。',
    'common.whatsappNotSetTitle': '请到 Dashboard → Website → Contact / WhatsApp 链接设置 WhatsApp',
    'form.thankYou': '感谢您 — 我们已收到您的消息。',
    'form.yourName': '您的姓名',
    'form.email': '邮箱',
    'form.submit': '提交',
    'form.sending': '发送中…',
    'form.notFound': '找不到表单或尚未发布。',
    'reviews.title': '顾客评价',
    'reviews.none': '尚无评价 — 成为第一位评价者。',
    'reviews.summary': '{avg} / 5 · {count} 条{unit}',
    'reviews.review': '评价',
    'reviews.reviews': '评价',
    'reviews.write': '撰写评价',
    'reviews.rating': '评分',
    'reviews.stars': '{n} 星',
    'reviews.bodyPlaceholder': '您的体验（选填）',
    'reviews.submit': '提交评价',
    'reviews.submitting': '提交中…',
    'reviews.moderatedNote': '评价经审核后才会显示。',
    'reviews.nameRequired': '请输入您的姓名',
    'reviews.submitSuccess': '评价已提交',
    'reviews.submitFailed': '无法提交评价',
    'cart.title': '购物车',
    'cart.empty': '您的购物车是空的。',
    'cart.continueShopping': '继续购物',
    'cart.continueShoppingBack': '← 继续购物',
    'cart.priceEach': '每件 {price}',
    'cart.remove': '移除',
    'cart.subtotal': '小计',
    'cart.bundle': '组合优惠：{name}',
    'cart.orderTotal': '订单总额',
    'cart.proceedToCheckout': '前往结账',
    'cart.signInToCheckout': '登录后结账',
    'cart.checkoutUnavailable': '此商店目前无法使用在线结账。',
    'cart.contactToComplete': '请联系商店以完成订单',
    'cart.updateQtyFailed': '无法更新数量',
    'checkout.progressAria': '结账进度',
    'checkout.stepCart': '购物车',
    'checkout.stepShipping': '配送',
    'checkout.stepPayment': '付款',
    'checkout.stepConfirmation': '确认',
    'checkout.titleShipping': '配送',
    'checkout.titlePayment': '付款',
    'checkout.delivery': '配送方式',
    'checkout.shipToAddress': '送货上门',
    'checkout.selfPickup': '到店自取',
    'checkout.mixedPickupShipHint':
      '当所有商品均支持取货时可选择到店自取。若选择送货，仅支持取货的商品也可由快递运送。',
    'checkout.pickupLocation': '取货地点',
    'checkout.deliveryInstructions': '配送说明（选填）',
    'checkout.noShippingRequired': '此订单无需填写配送信息。',
    'checkout.backToCart': '返回购物车',
    'checkout.backToCartArrow': '← 返回购物车',
    'checkout.continueToPayment': '继续至付款',
    'checkout.orderSummary': '订单摘要',
    'checkout.bundleSavings': '组合优惠',
    'checkout.couponApplied': '优惠码（{code}）',
    'checkout.total': '总额',
    'checkout.couponCode': '优惠码',
    'checkout.couponPlaceholder': '输入优惠码',
    'checkout.couponApply': '应用',
    'checkout.couponChecking': '检查中…',
    'checkout.couponAppliedMsg': '已应用优惠码',
    'checkout.couponFailed': '无法应用优惠码',
    'checkout.paymentMethod': '付款方式',
    'checkout.payWith': '使用 {name} 付款',
    'checkout.payWithTest': '{name}（测试）',
    'checkout.payWithDemo': '{name}（演示 — 不会真实扣款）',
    'checkout.checkoutUnavailable': '目前无法使用在线结账。',
    'checkout.backToShipping': '← 返回配送',
    'checkout.pleaseWait': '请稍候…',
    'checkout.placeOrderFps': '下单并以转数快付款',
    'checkout.paySecurely': '以 {name} 安全付款',
    'checkout.contactStore': '联系商店',
    'checkout.creatingOrder': '正在创建订单…',
    'checkout.connectingPayment': '正在连接安全付款…',
    'checkout.doNotClose': '请勿关闭此窗口。',
    'checkout.overlayDefault': '正在准备安全付款…',
    'checkout.uatTitle': '测试模式 — 仅限 UAT',
    'checkout.uatBody':
      '此商店正使用 KPay 沙盒（UAT）环境。此处付款仅供测试，不会产生真实收费。',
    'checkout.dismiss': '关闭',
    'checkout.paymentRedirectMissing': '未取得付款重定向',
    'checkout.validation.pickupLocation': '请选择取货地点',
    'checkout.validation.shippingAddress': '请输入配送地址',
    'checkout.validation.addressLine1': '请输入地址第一行',
    'checkout.validation.city': '请输入城市',
    'checkout.validation.country': '请选择国家／地区',
    'checkout.validation.noCountries': '此商店目前不接受任何国家／地区的送货',
    'checkout.validation.countryNotAllowed': '我们不配送至所选国家／地区',
    'checkout.validation.phoneAreaCode': '请选择区号',
    'checkout.validation.phoneInvalid':
      '请输入有效电话号码：使用国际格式（+国家代码及号码，最多 15 位数字）或 4–15 位本地号码',
    'checkout.address.recipientName': '收件人姓名',
    'checkout.address.fullName': '全名',
    'checkout.address.phone': '电话',
    'checkout.address.phonePlaceholder': '例如 91234567',
    'checkout.address.line1': '地址第一行',
    'checkout.address.line1Placeholder': '街道地址、邮政信箱等',
    'checkout.address.line2': '地址第二行',
    'checkout.address.line2Placeholder': '公寓、套房、单位、大厦、楼层等',
    'checkout.address.city': '城市',
    'checkout.address.stateProvince': '州／省',
    'checkout.address.statePlaceholder': '州或省',
    'checkout.address.postalCode': '邮政编码',
    'checkout.address.postalPlaceholder': '邮政编码',
    'checkout.address.country': '国家／地区',
    'checkout.address.selectCountry': '选择国家／地区',
    'checkout.error.cart_empty.title': '购物车是空的',
    'checkout.error.cart_empty.message': '结账前请先将商品加入购物车。',
    'checkout.error.cart_session_required.title': '购物车会话已过期',
    'checkout.error.cart_session_required.message': '请刷新此页面后再试。',
    'checkout.error.cart_outdated.title': '购物车需要重新确认',
    'checkout.error.cart_outdated.message': '请打开购物车查看更新后的选项与价格，然后再试一次结账。',
    'checkout.error.payment_unavailable.title': '无法付款',
    'checkout.error.payment_unavailable.message': '此商店目前不接受在线付款。',
    'checkout.error.payment_not_configured.title': '尚未设置付款',
    'checkout.error.payment_not_configured.message': '请联系商店以获取协助。',
    'checkout.error.payment_config.title': '无法开始付款',
    'checkout.error.payment_config.message':
      '您的商品仍在购物车中。请稍后再试，或联系商店寻求协助。',
    'checkout.error.payment_gateway.title': '付款服务商错误',
    'checkout.error.payment_gateway.message':
      '您的商品仍在购物车中。请再试一次，或联系商店以其他方式付款。',
    'checkout.error.order_invalid.title': '无法准备订单',
    'checkout.error.order_invalid.message': '请检查购物车后再试。',
    'checkout.error.shop_not_found.title': '找不到商店',
    'checkout.error.shop_not_found.message': '找不到您要寻找的商店。',
    'checkout.error.login_required.title': '需要登录',
    'checkout.error.login_required.message': '请先登录以继续结账。您的购物车会保留。',
    'checkout.error.server_error.title': '结账失败',
    'checkout.error.server_error.message': '发生错误。您的商品仍在购物车中 — 请稍后再试。',
    'checkout.error.network.title': '连接问题',
    'checkout.error.network.message':
      '无法连接到商店。请检查网络连接后再试。您的购物车已保存。',
    'checkout.confirm.title': '订单已确认',
    'checkout.confirm.thankYou': '感谢您的订购。',
    'checkout.confirm.orderLabel': '订单 {code}',
    'checkout.confirm.total': '总额：{currency} ${amount}',
    'checkout.confirm.paymentReceived': '已收到付款 — 谢谢！',
    'checkout.confirm.completePayment': '请在下方完成付款以确认订单。',
    'checkout.confirm.continueShopping': '继续购物',
    'checkout.confirm.viewCart': '查看购物车',
    'checkout.confirm.invalidOrder': '无效的订单编号。',
    'checkout.confirm.returnToCart': '返回购物车',
    'checkout.fps.preparing': '正在准备转数快付款…',
    'checkout.fps.title': '以转数快（FPS）付款',
    'checkout.fps.scanPay': '扫码支付 HK${amount}。参考编号：{code}',
    'checkout.fps.manualPay': '请以转数快转账 HK${amount}。在备注／参考编号填写 {code}。',
    'checkout.fps.qrAlt': '转数快付款二维码',
    'checkout.fps.manualTransfer': '转数快转账资料',
    'checkout.fps.bankCode': '银行代码',
    'checkout.fps.accountNumber': '账户号码',
    'checkout.fps.amountLabel': '金额',
    'checkout.fps.referenceLabel': '参考编号',
    'checkout.fps.afterPay': '转数快不会自动确认付款。付款后，商店会将订单标为已付款。如需支持，请保留订单参考编号。',
    'checkout.fps.unavailableTitle': '无法使用转数快付款',
    'checkout.fps.unavailableBody': '此商店可能尚未设置转数快，或此订单没有未付余额。',
    'checkout.return.confirming': '正在确认您的付款…',
    'checkout.return.missingOrder':
      '找不到您的订单编号。若您已完成付款，请携带收据联系商店。',
    'checkout.return.timeout': '付款确认时间较长。请稍后再点“再次检查付款”。',
    'checkout.return.statusUnavailableMsg': '目前无法确认您的付款状态。',
    'checkout.return.statusUnavailableTitle': '无法取得付款状态',
    'checkout.return.checkAgain': '再次检查付款',
    'checkout.return.checking': '正在检查付款…',
    'checkout.return.backToShop': '返回商店',
    'checkout.return.thankYouTickets': '谢谢 — 您的门票已准备好！',
    'checkout.return.thankYouOrder': '感谢您的订购！',
    'checkout.return.orderPaid': '订单 {code} 已付款',
    'checkout.return.orderPaidAmount': '订单 {code} 已付款（{currency} {amount}）。',
    'checkout.return.ticketsHint': '请下载或打印下方门票。入场时亦可出示二维码。',
    'checkout.return.processSoon': '我们将尽快处理您的订单。',
    'checkout.return.orderDetails': '订单详情',
    'checkout.return.yourTickets': '您的门票',
    'checkout.return.qty': '数量 {qty}',
    'checkout.return.lineTotal': '{currency} {amount}',
    'checkout.return.orderTotal': '合计',
    'checkout.return.downloadTicket': '下载 PDF',
    'checkout.return.ticket': '门票',
    'checkout.return.ticketSeat': '门票 — {seat}',
    'checkout.return.ticketMeta': '{event} · {tier}',
    'checkout.return.ticketMetaEvent': '{event}',
    'checkout.return.continueShoppingSecondary': '继续购物',
    'checkout.return.paymentFailedTitle': '付款失败',
    'checkout.return.paymentFailedBody': '订单 {code} 未能完成付款。',
    'checkout.return.tryAgainHint': '您可以返回购物车，改用其他付款方式再试。',
    'checkout.return.notCompletedTitle': '尚未完成付款',
    'checkout.return.processingTitle': '付款处理中',
    'checkout.return.notCompletedBody':
      '订单 {code} 尚未标记为已付款。若您已在付款服务商完成付款，请点下方“再次检查付款”。若您在付款页取消，可返回购物车再试。',
    'checkout.return.processingBody':
      '订单 {code} 正在确认中。若您已完成付款，更新可能需要一点时间 — 您稍后可刷新此页。',
    'checkout.return.refreshStatus': '刷新状态',
    'support.needHelp': '需要帮助？',
    'support.close': '关闭',
    'support.title': '客户支持',
    'support.hoursHint': '我们通常在营业时间回复',
    'support.subjectPlaceholder': '主题（选填）',
    'support.bodyPlaceholder': '我们如何帮助您？',
    'support.back': '返回',
    'support.send': '发送',
    'support.sending': '发送中…',
    'support.allChats': '所有对话',
    'support.new': '新建',
    'support.newConversation': '新对话',
    'support.noConversations': '尚无对话。',
    'support.askQuestion': '提出问题',
    'support.conversation': '对话',
    'support.typeMessage': '输入消息…',
    'support.replyToReopen': '回复以重新打开此对话…',
    'support.attachHint': '可附加截图、PDF、Excel 或 Word（最多 {{max}} 个文件，每个 10MB）',
    'support.attachFiles': '附加文件',
    'support.noMessages': '暂无消息。',
    'support.sendFailed': '发送失败',
    'support.maxFiles': '每条消息最多 {{max}} 个文件',
    'support.fileTooLarge': '{{name}} 超过 10MB',
    'demopay.title': '演示付款',
    'demopay.bannerTitle': '演示付款 — 不会真实扣款',
    'demopay.bannerBody': '此为演示用模拟付款。点击“付款”会标记订单已付；点击“取消”则模拟付款失败。',
    'demopay.orderLabel': '订单 {code}',
    'demopay.amountHint': '应付款项（模拟）',
    'demopay.pay': '付款（模拟成功）',
    'demopay.cancel': '取消（模拟失败）',
    'demopay.working': '请稍候…',
    'demopay.missingSession': '此演示付款链接无效或已丢失。',
    'demopay.loadFailed': '无法加载演示付款会话。',
    'demopay.payFailed': '无法完成演示付款。',
    'demopay.cancelFailed': '无法取消演示付款。',
    'demopay.backToShop': '返回商店',
  },
}
